import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import MainLayout from '../layouts/MainLayout';
import { Plus, Edit, Trash2, Search, FolderOpen, Upload, X } from 'lucide-react';
import * as categoryService from '../services/categoryService';

interface Category {
  id?: string;
  _id?: string;
  slug?: string;
  name?: {
    tr: string;
    en: string;
    es: string;
  };
  title?: {
    tr: string;
    en: string;
    es: string;
  } | string;
  description?: {
    tr: string;
    en: string;
    es: string;
  } | string;
  image?: string;
  createdAt?: string;
  updatedAt?: string;
}

const dummyCategories: Category[] = [
  {
    id: '1',
    name: {
      tr: 'PE Borular',
      en: 'PE Pipes',
      es: 'Tuberías PE'
    },
    description: {
      tr: 'Polietilen borular ve bağlantı elemanları',
      en: 'Polyethylene pipes and fittings',
      es: 'Tuberías de polietileno y accesorios'
    },
    createdAt: '2023-01-15'
  },
  {
    id: '2',
    name: {
      tr: 'PPR Borular',
      en: 'PPR Pipes',
      es: 'Tuberías PPR'
    },
    description: {
      tr: 'Polipropilen borular ve bağlantı sistemleri',
      en: 'Polypropylene pipes and connection systems',
      es: 'Tuberías de polipropileno y sistemas de conexión'
    },
    createdAt: '2023-02-10'
  },
  {
    id: '3',
    name: {
      tr: 'PVC Borular',
      en: 'PVC Pipes',
      es: 'Tuberías PVC'
    },
    description: {
      tr: 'PVC borular ve drenaj sistemleri',
      en: 'PVC pipes and drainage systems',
      es: 'Tuberías PVC y sistemas de drenaje'
    },
    createdAt: '2023-03-05'
  },
  {
    id: '4',
    name: {
      tr: 'Fittings',
      en: 'Fittings',
      es: 'Accesorios'
    },
    description: {
      tr: 'Boru bağlantı elemanları ve aksesuarları',
      en: 'Pipe connection elements and accessories',
      es: 'Elementos de conexión de tuberías y accesorios'
    },
    createdAt: '2023-04-12'
  },
  {
    id: '5',
    name: {
      tr: 'Vanalar',
      en: 'Valves',
      es: 'Válvulas'
    },
    description: {
      tr: 'Su kontrol vanaları ve regülatörleri',
      en: 'Water control valves and regulators',
      es: 'Válvulas de control de agua y reguladores'
    },
    createdAt: '2023-05-20'
  }
];

const Categories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<'tr' | 'en' | 'es'>('tr');
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [formValues, setFormValues] = useState({
    name: { tr: '', en: '', es: '' },
    description: { tr: '', en: '', es: '' }
  });
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<{
    image?: File;
  }>({});

  // Kategorileri API'den yükle
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await categoryService.getCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      // API çalışmıyorsa dummy data kullan
      setCategories(dummyCategories);
      toast.error('Kategoriler yüklenirken hata oluştu. Dummy data kullanılıyor.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    
    // API'den gelen veriyi form formatına çevir
    const nameData = category.title || category.name || { tr: '', en: '', es: '' };
    const descData = category.description || { tr: '', en: '', es: '' };
    
    setFormValues({
      name: typeof nameData === 'string' ? { tr: nameData, en: '', es: '' } : nameData,
      description: typeof descData === 'string' ? { tr: descData, en: '', es: '' } : descData
    });
    
    // Mevcut görsel varsa önizleme olarak ayarla (API'den gelen gerçek URL'yi kullan)
    if (category.image && category.image !== 'string' && category.image !== null) {
      setImagePreview(category.image);
    } else {
      setImagePreview(null);
    }
    
    setShowForm(true);
    setHasChanges(false);
  };

  const handleNewCategory = () => {
    setEditingCategory(null);
    setFormValues({
      name: { tr: '', en: '', es: '' },
      description: { tr: '', en: '', es: '' }
    });
    setImagePreview(null);
    setSelectedFiles({});
    setShowForm(true);
    setHasChanges(false);
  };

  const handleCloseForm = () => {
    if (hasChanges) {
      if (window.confirm('Kaydedilmemiş değişiklikler var. Çıkmak istediğinizden emin misiniz?')) {
        // Blob URL'leri temizle
        if (imagePreview && imagePreview.startsWith('blob:')) {
          URL.revokeObjectURL(imagePreview);
        }
        setShowForm(false);
        setEditingCategory(null);
        setImagePreview(null);
        setSelectedFiles({});
        setHasChanges(false);
      }
    } else {
      // Blob URL'leri temizle
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
      setShowForm(false);
      setEditingCategory(null);
      setImagePreview(null);
      setSelectedFiles({});
    }
  };

  const handleDelete = async (categoryId: string) => {
    if (window.confirm('Bu kategoriyi silmek istediğinizden emin misiniz?')) {
      try {
        console.log("Deleting category with ID:", categoryId);
        await categoryService.deleteCategory(categoryId);
        toast.success('Kategori başarıyla silindi');
        fetchCategories(); // Listeyi yenile
      } catch (error: any) {
        console.error('Error deleting category:', error);
        if (error.response && error.response.data) {
          const errorMessage = error.response.data.message || error.response.data.detail || 'Kategori silinirken hata oluştu';
          toast.error(errorMessage);
        } else {
          toast.error('Kategori silinirken hata oluştu');
        }
      }
    }
  };

  // Görsel yükleme fonksiyonları
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Dosya boyutu kontrolü (2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Dosya boyutu 2MB\'dan küçük olmalıdır');
        return;
      }

      // Dosya tipi kontrolü
      if (!file.type.startsWith('image/')) {
        toast.error('Sadece görsel dosyaları yükleyebilirsiniz');
        return;
      }

      // Eski blob URL'yi temizle
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }

      // Yeni önizleme oluştur
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      setSelectedFiles(prev => ({ ...prev, image: file }));
      setHasChanges(true);
    }
  };

  const handleRemoveImage = () => {
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
    setSelectedFiles(prev => ({ ...prev, image: undefined }));
    setHasChanges(true);
  };

  const handleImageUpload = async (categoryId: string) => {
    if (selectedFiles.image) {
      try {
        console.log("Uploading category image for ID:", categoryId);
        await categoryService.uploadCategoryImage(categoryId, selectedFiles.image);
        console.log("Category image uploaded successfully");
        return true;
      } catch (error) {
        console.error("Error uploading category image:", error);
        toast.error('Görsel yüklenirken hata oluştu');
        return false;
      }
    }
    return true;
  };

  const validateCategoryForm = (categoryData: any): { isValid: boolean; errors: {[key: string]: string}; messages: string[] } => {
    const errors: {[key: string]: string} = {};
    const messages: string[] = [];
    
    // Türkçe isim kontrolü
    if (!categoryData.name.tr.trim()) {
      errors['name_tr'] = 'Türkçe kategori ismi zorunludur';
      messages.push('Türkçe kategori ismi girilmelidir');
    }
    
    // Türkçe açıklama kontrolü
    if (!categoryData.description.tr.trim()) {
      errors['description_tr'] = 'Türkçe açıklama zorunludur';
      messages.push('Türkçe açıklama girilmelidir');
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors,
      messages
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setFormErrors({});
      setValidationErrors([]);
      
      const categoryData = {
        name: formValues.name,
        description: formValues.description
      };

      // Form validation
      const validation = validateCategoryForm(categoryData);
      if (!validation.isValid) {
        setFormErrors(validation.errors);
        setValidationErrors(validation.messages);
        toast.error('Lütfen gerekli alanları doldurun');
        return;
      }

      let categoryId;
      if (editingCategory) {
        await categoryService.updateCategory(editingCategory.id, categoryData);
        categoryId = editingCategory.id;
        toast.success('Kategori başarıyla güncellendi');
      } else {
        const result = await categoryService.createCategory(categoryData);
        categoryId = result._id || result.id;
        toast.success('Kategori başarıyla oluşturuldu');
      }

      // Görsel yükleme
      if (categoryId && selectedFiles.image) {
        const uploadSuccess = await handleImageUpload(categoryId);
        if (!uploadSuccess) {
          toast.error('Kategori kaydedildi ancak görsel yüklenemedi');
        }
      }
      
      // Blob URL'leri temizle
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
      
      setShowForm(false);
      setEditingCategory(null);
      setImagePreview(null);
      setSelectedFiles({});
      setHasChanges(false);
      fetchCategories(); // Listeyi yenile
    } catch (error: any) {
      console.error('Error saving category:', error);
      
      // API'den gelen detaylı hata mesajlarını parse et
      if (error.response && error.response.data) {
        const apiErrors = error.response.data;
        
        if (apiErrors.detail && Array.isArray(apiErrors.detail)) {
          // FastAPI validation errors
          const newErrors: {[key: string]: string} = {};
          const newMessages: string[] = [];
          
          apiErrors.detail.forEach((err: any) => {
            if (err.loc && err.msg) {
              const field = err.loc[err.loc.length - 1]; // Son eleman field name
              newErrors[field] = err.msg;
              newMessages.push(`${field}: ${err.msg}`);
            }
          });
          
          setFormErrors(newErrors);
          setValidationErrors(newMessages);
          toast.error('Lütfen form hatalarını düzeltin');
        } else if (apiErrors.message) {
          setValidationErrors([apiErrors.message]);
          toast.error(apiErrors.message);
        } else {
          setValidationErrors(['Sunucu hatası oluştu']);
          toast.error('Sunucu hatası oluştu');
        }
      } else {
        setValidationErrors(['Ağ hatası oluştu. Lütfen tekrar deneyin.']);
        toast.error('Ağ hatası oluştu. Lütfen tekrar deneyin.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const filteredCategories = categories.filter(category => {
    const nameText = typeof category.name === 'string' ? category.name : (category.name?.tr || '');
    const descText = typeof category.description === 'string' ? category.description : (category.description?.tr || '');
    return nameText.toLowerCase().includes(searchTerm.toLowerCase()) ||
           descText.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (showForm) {
    return (
      <MainLayout>
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {editingCategory ? 'Kategori Düzenle' : 'Yeni Kategori'}
              </h1>
              <p className="text-gray-600 mt-2">
                Kategori bilgilerini ve açıklamalarını yönetin
              </p>
            </div>
            <button
              onClick={handleCloseForm}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6">
              {/* Language Tabs */}
              <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
                {(['tr', 'en', 'es'] as const).map((lang) => (
                  <button
                    key={lang}
                    onClick={() => setSelectedLanguage(lang)}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                      selectedLanguage === lang
                        ? 'bg-white text-primary-600 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {lang.toUpperCase()}
                  </button>
                ))}
              </div>

              <form 
                onSubmit={handleSubmit}
                className="space-y-6"
              >

                {/* Category Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kategori Adı ({selectedLanguage.toUpperCase()})
                  </label>
                  <input
                    type="text"
                    value={formValues.name[selectedLanguage]}
                    onChange={(e) => {
                      setFormValues(prev => ({
                        ...prev,
                        name: {
                          ...prev.name,
                          [selectedLanguage]: e.target.value
                        }
                      }));
                      setHasChanges(true);
                      // Clear error when user starts typing
                      if (formErrors[`name_${selectedLanguage}`]) {
                        setFormErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors[`name_${selectedLanguage}`];
                          return newErrors;
                        });
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      formErrors[`name_${selectedLanguage}`] 
                        ? 'border-red-300 bg-red-50' 
                        : 'border-gray-300'
                    }`}
                    placeholder="Kategori adını girin"
                    required
                  />
                  {formErrors[`name_${selectedLanguage}`] && (
                    <p className="mt-1 text-sm text-red-600">{formErrors[`name_${selectedLanguage}`]}</p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Açıklama ({selectedLanguage.toUpperCase()})
                  </label>
                  <textarea
                    rows={4}
                    value={formValues.description[selectedLanguage]}
                    onChange={(e) => {
                      setFormValues(prev => ({
                        ...prev,
                        description: {
                          ...prev.description,
                          [selectedLanguage]: e.target.value
                        }
                      }));
                      setHasChanges(true);
                      // Clear error when user starts typing
                      if (formErrors[`description_${selectedLanguage}`]) {
                        setFormErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors[`description_${selectedLanguage}`];
                          return newErrors;
                        });
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      formErrors[`description_${selectedLanguage}`] 
                        ? 'border-red-300 bg-red-50' 
                        : 'border-gray-300'
                    }`}
                    placeholder="Kategori açıklamasını girin"
                  />
                  {formErrors[`description_${selectedLanguage}`] && (
                    <p className="mt-1 text-sm text-red-600">{formErrors[`description_${selectedLanguage}`]}</p>
                  )}
                </div>

                {/* Category Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kategori Görseli
                  </label>
                  <div 
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-primary-400 transition-colors"
                    onClick={() => document.getElementById('category-image-input')?.click()}
                  >
                    {imagePreview ? (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Önizleme"
                          className="max-w-full max-h-48 mx-auto rounded-lg shadow-sm"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImage();
                          }}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        <p className="text-sm text-gray-600 mt-2">
                          {selectedFiles.image?.name || 'Mevcut görsel'}
                        </p>
                      </div>
                    ) : (
                      <>
                        <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-sm text-gray-600 mb-2">
                          Kategori görselini yükleyin
                        </p>
                        <button
                          type="button"
                          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Görsel Seç
                        </button>
                        <p className="text-xs text-gray-500 mt-2">
                          PNG, JPG formatlarında, maksimum 2MB
                        </p>
                      </>
                    )}
                  </div>
                  <input
                    id="category-image-input"
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>

                {/* Validation Errors */}
                {validationErrors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <X className="h-5 w-5 text-red-400" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">
                          Form Hataları
                        </h3>
                        <div className="mt-2 text-sm text-red-700">
                          <ul className="list-disc pl-5 space-y-1">
                            {validationErrors.map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCloseForm}
                    disabled={submitting}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium disabled:opacity-50"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Kaydediliyor...' : (editingCategory ? 'Güncelle' : 'Oluştur')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (loading) {
    return (
      <MainLayout>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Kategoriler yükleniyor...</p>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Kategori Yönetimi</h1>
            <p className="text-gray-600 mt-2">
              Ürün kategorilerini yönetin ve düzenleyin
            </p>
          </div>
          <button
            onClick={handleNewCategory}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
          >
            <Plus className="h-5 w-5 mr-2" />
            Yeni Kategori
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 bg-primary-100 rounded-lg">
                <FolderOpen className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Toplam Kategori</p>
                <p className="text-2xl font-bold text-gray-900">{categories.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <Edit className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Aktif Kategori</p>
                <p className="text-2xl font-bold text-gray-900">
                  {categories.filter(cat => cat.image && cat.image !== 'string').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="p-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Kategori ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Categories Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Kategoriler ({filteredCategories.length})
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kategori
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Açıklama
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Oluşturulma
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCategories.map((category) => (
                  <tr key={category.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {category.image && category.image !== 'string' && category.image !== null ? (
                            <img
                              className="h-10 w-10 rounded-lg object-cover border border-gray-200"
                              src={category.image}
                              alt={typeof category.name === 'string' ? category.name : (category.name?.tr || '')}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const parent = target.parentElement;
                                if (parent) {
                                  parent.innerHTML = '<div class="h-10 w-10 rounded-lg bg-gray-200 flex items-center justify-center"><svg class="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5a2 2 0 012-2h4a2 2 0 012 2v1H8V5z"></path></svg></div>';
                                }
                              }}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-lg bg-gray-200 flex items-center justify-center">
                              <FolderOpen className="h-5 w-5 text-gray-500" />
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {typeof category.name === 'string' ? category.name : (category.name?.tr || '')}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {typeof category.description === 'string' ? category.description : (category.description?.tr || '')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {category.createdAt ? new Date(category.createdAt).toLocaleDateString('tr-TR') : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEdit(category)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            const categoryId = category._id || category.id;
                            if (categoryId) {
                              handleDelete(categoryId);
                            } else {
                              toast.error('Kategori ID bulunamadı');
                            }
                          }}
                          className="text-red-600 hover:text-red-900"
                          disabled={!category._id && !category.id}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Categories;

